'use client';

interface Series {
  key: string;
  label: string;
  color: string;
}

interface LineChartProps {
  data: Record<string, string | number>[];
  xKey: string;
  series: Series[];
  height?: number;
}

export function LineChart({ data, xKey, series, height = 120 }: LineChartProps) {
  if (data.length < 2) return null;

  const W = 500;
  const H = height;
  const PAD = { top: 8, right: 16, bottom: 28, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  // gather all numeric values to compute shared y-domain
  const allVals = series.flatMap((s) =>
    data.map((d) => parseFloat(String(d[s.key] ?? 0))).filter((v) => !isNaN(v))
  );
  const yMin = Math.min(...allVals);
  const yMax = Math.max(...allVals);
  const yRange = yMax - yMin || 1;

  function xPos(i: number) {
    return PAD.left + (i / (data.length - 1)) * chartW;
  }
  function yPos(val: number) {
    return PAD.top + chartH - ((val - yMin) / yRange) * chartH;
  }

  function polyline(seriesKey: string) {
    return data
      .map((d, i) => {
        const v = parseFloat(String(d[seriesKey] ?? 0));
        if (isNaN(v)) return null;
        return `${xPos(i).toFixed(1)},${yPos(v).toFixed(1)}`;
      })
      .filter(Boolean)
      .join(' ');
  }

  // x-axis labels: show every N-th to avoid overlap
  const step = Math.ceil(data.length / 8);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ height }}
      aria-label="Line chart"
    >
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = PAD.top + t * chartH;
        const val = yMax - t * yRange;
        return (
          <g key={t}>
            <line
              x1={PAD.left}
              y1={y}
              x2={W - PAD.right}
              y2={y}
              stroke="#e2e8f0"
              strokeWidth="1"
            />
            <text x={PAD.left - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">
              {Math.round(val)}
            </text>
          </g>
        );
      })}

      {/* X labels */}
      {data.map((d, i) => {
        if (i % step !== 0 && i !== data.length - 1) return null;
        return (
          <text
            key={i}
            x={xPos(i)}
            y={H - 4}
            textAnchor="middle"
            fontSize="9"
            fill="#94a3b8"
          >
            {String(d[xKey]).slice(2)}
          </text>
        );
      })}

      {/* Series lines */}
      {series.map((s) => (
        <polyline
          key={s.key}
          points={polyline(s.key)}
          fill="none"
          stroke={s.color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}

      {/* Dots */}
      {series.map((s) =>
        data.map((d, i) => {
          const v = parseFloat(String(d[s.key] ?? 0));
          if (isNaN(v)) return null;
          return (
            <circle
              key={`${s.key}-${i}`}
              cx={xPos(i)}
              cy={yPos(v)}
              r="2.5"
              fill={s.color}
            />
          );
        })
      )}
    </svg>
  );
}
